import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import ObjectHistory, { ObjectHistoryType } from "./ObjectHistory";
import { useObjectHistoryContext } from "./ObjectHistoryContext";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import ObjectHistoryOptions from "./ObjectHistoryOptions";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { IeLynxObject } from "../../common/models/eLynxObject";

export type ObjectHistoryTabContentProps = {
    visible?: boolean;
    noAlarms?: boolean;
    noDiagnosticEvents?: boolean;
    noDataQualityEvents?: boolean;
    noFormEvents?: boolean;
    noManualEvents?: boolean;
    noMemos?: boolean;
    noSettingsChanges?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
    hasAlarmHistory?: (hasAlarmHistory: boolean) => void;
    hasSettingsChanges?: (hasSettingsChanges: boolean) => void;
};

export type ObjectHistoryTabContentType = {
    executeAction: (action: BaseActionEnum | BaseActionEnum) => void;
};

function ObjectHistoryTabContent({
    visible,
    noAlarms,
    noDiagnosticEvents,
    noDataQualityEvents,
    noFormEvents,
    noManualEvents,
    noMemos,
    noSettingsChanges,
    setViewOptionState,
    hasAlarmHistory,
    hasSettingsChanges
}: ObjectHistoryTabContentProps, ref: ForwardedRef<ObjectHistoryTabContentType> | undefined) {

    const routeContext = useRouteContext();
    const objectHistoryRef = useRef<ObjectHistoryType>(null);

    useImperativeHandle<ObjectHistoryTabContentType, ObjectHistoryTabContentType>(
        ref,
        () => ({
            executeAction: (action: BaseActionEnum | BaseActionEnum) =>
                // Bug 53223: Device Details - Add Memo does not open New Memo modal
                // when on a different tab, this view does not render so the ref is null so
                // if action to add memo is selected it does nothing.  This timeout will allow
                // the view to render before attempting to execute the action
                setTimeout(() => {
                    objectHistoryRef.current?.executeAction(action);    
                }, 1000)
                
        })
    );

    const {
        state,
        resetOptions
    } = useObjectHistoryContext();


    useEffect(() => {

        hasAlarmHistory && hasAlarmHistory(state.hasAlarmHistory);
        hasSettingsChanges && hasSettingsChanges(state.hasSettingsHistory);

    }, [
        hasAlarmHistory,
        hasSettingsChanges,
        state.downloadAlarmsUid,
        state.hasAlarmHistory,
        state.hasSettingsHistory
    ])

    return (
        <>
            {visible &&
                <ContentContainer>

                    <ContentSection>
                        <ObjectHistory
                            visible={visible}
                            groupID={routeContext.groupID as number}
                            contextObject={routeContext.contextObject as IeLynxObject}
                            ref={objectHistoryRef}
                            setViewOptionState={setViewOptionState}
                        />
                    </ContentSection>

                    <OptionsSideSheet onReset={() => resetOptions()}>
                        <ObjectHistoryOptions
                            noAlarms={noAlarms}
                            noDiagnosticEvents={noDiagnosticEvents}
                            noDataQualityEvents={noDataQualityEvents}
                            noFormEvents={noFormEvents}
                            noManualEvents={noManualEvents}
                            noMemos={noMemos}
                            noSettingsChanges={noSettingsChanges}
                            onShowDurationSettings={() =>
                                objectHistoryRef.current?.executeAction(BaseActionEnum.ShowDurationSettings)
                            }
                        />
                    </OptionsSideSheet>


                </ContentContainer >
            }
        </>
    );
}

export default forwardRef(ObjectHistoryTabContent);